import { TAppDispatch, TRootState } from 'store/types'
import voiceCatalogSlice from 'store/features/voiceCatalog/slice'
import { voiceCatalogSelectors } from 'store/features/voiceCatalog/selectors'
import { userSelectors } from 'store/features/user/selectors'
import {
  getTtsLanguageOptions_API,
  getTtsSpeakers_API,
  getTtsSpeakerTones_API
} from 'api/tts'
import { ErrorKeys } from 'consts/errorKeys'
import { captureException } from '@sentry/react'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { handleApiError } from 'store/utils'
import { getFirstSpeakerIdForUser, getSpeakersCacheKey } from 'utils/tts'
import { ITtsSpeaker } from 'types/text-to-speech'
import { AVATAR_TTS_VOICE_MODEL } from 'consts/tts'

const { actions } = voiceCatalogSlice

const getLanguageOptions =
  () => async (dispatch: TAppDispatch, getState: () => TRootState) => {
    const langOptions = voiceCatalogSelectors.languageOptionsData(getState())
    const isLoading = voiceCatalogSelectors.isLanguageOptionsLoading(getState())
    if (langOptions?.length || isLoading) {
      return
    }

    dispatch(actions.setLanguageOptionsIsLoading(true))
    try {
      const response = await getTtsLanguageOptions_API()
      dispatch(actions.setLanguageOptions(response.data))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(actions.setLanguageOptionsIsLoading(false))
    }
  }

// Like `getLanguageOptions`, but fetches the list filtered by the AI Avatar's
// voice model and caches it in a separate slot so the avatar and regular TTS
// flows don't share/clobber each other's language lists.
const getAvatarLanguageOptions =
  () => async (dispatch: TAppDispatch, getState: () => TRootState) => {
    const langOptions =
      voiceCatalogSelectors.avatarLanguageOptionsData(getState())
    const isLoading =
      voiceCatalogSelectors.isAvatarLanguageOptionsLoading(getState())
    if (langOptions?.length || isLoading) {
      return
    }

    dispatch(actions.setAvatarLanguageOptionsIsLoading(true))
    try {
      const response = await getTtsLanguageOptions_API(AVATAR_TTS_VOICE_MODEL)
      dispatch(actions.setAvatarLanguageOptions(response.data))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(actions.setAvatarLanguageOptionsIsLoading(false))
    }
  }

// Picks the first usable speaker for the current user (non-premium for free
// users) and reports it back so callers can auto-select a default voice.
const reportFirstSpeaker = (
  state: TRootState,
  speakers: ITtsSpeaker[] | null,
  onLoaded?: (firstSpeakerId: number) => void
) => {
  if (!speakers?.length || !onLoaded) {
    return
  }
  const isFreeUser = userSelectors.isFreeUser(state)
  onLoaded(getFirstSpeakerIdForUser(speakers, isFreeUser))
}

const getSpeakersPerLangCode =
  (
    langCode: string,
    onLoaded?: (firstSpeakerId: number) => void,
    // Non-default voice model (e.g. the avatar flow's `tts-worker`). Folded into
    // the cache key so it doesn't share the regular flow's speaker list.
    model?: string
  ) =>
  async (dispatch: TAppDispatch, getState: () => TRootState) => {
    const cacheKey = getSpeakersCacheKey(langCode, model)
    const state = getState()
    const speakers = voiceCatalogSelectors.speakersPerLangCodeSelector(
      state,
      cacheKey
    )
    const isLoading =
      voiceCatalogSelectors.speakersPerLangCodeIsLoadingSelector(
        state,
        cacheKey
      )

    if (isLoading) {
      return
    }

    if (speakers?.length) {
      reportFirstSpeaker(state, speakers, onLoaded)
      return
    }

    dispatch(
      actions.setSpeakersPerLangCodeIsLoading({
        langCode: cacheKey,
        isLoading: true
      })
    )
    try {
      const response = await getTtsSpeakers_API({ langCode, model })
      dispatch(
        actions.setSpeakersPerLangCode({
          langCode: cacheKey,
          speakers: response.data
        })
      )
      reportFirstSpeaker(getState(), response.data, onLoaded)
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(
        actions.setSpeakersPerLangCodeIsLoading({
          langCode: cacheKey,
          isLoading: false
        })
      )
    }
  }

const getSpeakerTones =
  () => async (dispatch: TAppDispatch, getState: () => TRootState) => {
    const isLoading = voiceCatalogSelectors.isSpeakerTonesLoading(getState())
    if (isLoading) {
      return
    }

    dispatch(actions.setSpeakerTonesIsLoading(true))
    try {
      const response = await getTtsSpeakerTones_API()
      dispatch(actions.setSpeakerTones(response.data))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      dispatch(actions.setSpeakerTonesIsLoading(false))
    }
  }

export const voiceCatalogThunks = {
  getLanguageOptions,
  getAvatarLanguageOptions,
  getSpeakersPerLangCode,
  getSpeakerTones
}
